

































































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import jQuery from 'jquery';

import {
    CONTRACTS,
    WindowsServiceContract,
    EventsServiceContract,
    Events,
} from '@/core/contracts';
import { throttle } from 'lodash';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { shorten, NoCache } from '@/core/helpers';
import {
    ContextsState,
    IndicatorsState,
    ProductsState,
    SettingsState,
} from '@/store';
import {
    CustomValue,
    CustomValueType,
} from '@/store/indicators';
import { indicatorsHandlers } from '@/pages/indicators';
import { localize } from '@/core/filters';
import { IndicatorItem } from './Indicators.IndicatorItem';
import { SettingsChangeServiceContract } from '../contracts/settingsChangeServiceContract';
import Icon from '@/core/components/controls/Icon.vue';
import { DxScrollView } from 'devextreme-vue/scroll-view';

enum TargetPart {
    Upper,
    Lower,
}

const RESIZE_THROTTLE_MS = 1000 / 30; // 30 fps

@Component({
    components: {
        Icon,
        DxScrollView,
    },
})
export default class Indicators extends Vue {
    private readonly eventSubsriptions = [
        {
            eventName: Events.ContextChanged,
            handler: this.onContextChanged,
        },
        {
            eventName: Events.ProcessingFinished,
            handler: this.onProcessingFinished,
        },
    ];
    private setListHeight = throttle(
        this.setListHeightThrottled.bind(this),
        RESIZE_THROTTLE_MS
    );

    @Inject(CONTRACTS.EventsService)
    private eventsService?: EventsServiceContract;
    @Inject(CONTRACTS.WindowsService)
    private windowsService?: WindowsServiceContract;

    private readonly indicatorItems: IndicatorItem[] = [];
    private componentSpacer: number = 16;
    private showScrollbars: string = 'never';
    private height: number = 0;

    private get showDnDTargetForEmptyList(): boolean {
        return this.indicators.length === 0;
    }

    private get indicators(): IndicatorItem[] {
        let result: IndicatorItem[];
        this.loadIndicatorsFromIndicatorsState();
        result = this.indicatorItems;
        return result;
    }

    public get isIndicatorsLoading() {
        return IndicatorsState.isLoading;
    }

    public localize(resourceName: string, key?: string): string {
        return localize(resourceName, key);
    }

    public openIndicator(indicator: IndicatorItem): void {
        const handler = indicatorsHandlers[indicator.name];

        if (typeof handler === 'function') {
            handler();
        } else {
            this.windowsService!.showDialog(
                `Handler for indicator '${indicator.name}' not implemented!`
            );
        }
    }

    @Watch('currentProduct')
    private onFilterChanged() {
        this.fetchIndicatorsfromIndicatorsState(false);
    }

    private get currentProduct() {
        return ProductsState.current;
    }

    private async mounted() {
        this.fetchIndicatorsfromIndicatorsState(true);
        window.addEventListener('resize', this.setListHeight);

        this.subscribeEvents();
    }

    private updated() {
        this.setListHeight();
    }

    private onContextChanged(): void {
        this.fetchIndicatorsfromIndicatorsState(true);
    }

    private onProcessingFinished({ contextId }: any): void {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.fetchIndicatorsfromIndicatorsState(false);
        }
    }

    @NoCache
    private get settingsChangeService(): SettingsChangeServiceContract {
        return SettingsState.changeService!;
    }

    private subscribeEvents() {
        this.eventSubsriptions.forEach(event => {
            this.eventsService!.on(event.eventName, event.handler);
        });
    }

    private setListHeightThrottled() {
        if (this.isIndicatorsLoading) {
            this.showScrollbars = 'never';
            this.height = 0;
            return;
        }

        const listName = 'indicatorsList';
        const list = this.$refs[listName] as Element;
        const contentName = 'listContent';
        const content = this.$refs[contentName] as Element;

        const contentBounding = list
            ? list.getBoundingClientRect()
            : { top: 0 };

        const listMaxHeight = list
            ? window.innerHeight - contentBounding.top - this.componentSpacer
            : 0;
        const listHeight = content ? content.clientHeight : 0;

        this.showScrollbars = listHeight !== 0 ? 'onScroll' : 'never';
        this.height = listHeight > listMaxHeight ? listMaxHeight : listHeight;
    }

    private beforeDestroy() {
        window.removeEventListener('resize', this.setListHeight);
        this.unsubscribeEvents();
    }

    /**
     * Unsubscribe from events according eventSubsriptions declarations
     */
    private unsubscribeEvents() {
        this.eventSubsriptions.forEach(subscription => {
            this.eventsService!.off(
                subscription.eventName,
                subscription.handler
            );
        });
    }

    /**
     * Loads indicators from IndicatorsState
     */
    private async fetchIndicatorsfromIndicatorsState(clear: boolean) {
        if (clear) {
            this.indicatorItems.splice(0);
        }
        await IndicatorsState.fetchIndicators();
        this.loadIndicatorsFromIndicatorsState();
    }

    /**
     * Loads indicators from IndicatorsState and prepares
     * indicatorItems for displaying indicators
     */
    private async loadIndicatorsFromIndicatorsState() {
        this.indicatorItems.splice(0);
        for (const indicator of IndicatorsState.list) {
            this.indicatorItems.push(new IndicatorItem(indicator));
        }
    }

    /**
     * Returns part of the indicator element where mouse is hit
     * @returns TargetPath.Upper when mouse hits upper part of
     * the indicator element and TargetPath.Lower when mouse hits
     * lower part of the indicator element.
     */
    private getTargetPart(evt: MouseEvent): TargetPart {
        if (!evt) {
            throw new Error('evt is not set');
        }
        if (!evt.currentTarget) {
            throw new Error('evt.currentTarget is not set');
        }

        let result: TargetPart;

        // Calculate offset of the element under cursor relative document
        const jTarget = jQuery(evt.currentTarget);
        const targetOffset = jTarget.offset() || { top: 0 };
        // And half height of the element under cursor
        const targetHalfHeight = (jTarget.height() || 0) / 2;
        // Event position on Y relative the element under cursor
        const eventOffset = evt.clientY + window.pageYOffset - targetOffset.top;

        if (eventOffset <= targetHalfHeight) {
            result = TargetPart.Upper;
        } else {
            result = TargetPart.Lower;
        }

        return result;
    }

    /**
     * Converts indicator names to lower camel case.
     * This is 'kostyl' (rus: костыль), because:
     * -- There is no single indicator identifier
     * -- There are different ways to name indicators in different APIs
     */
    private toLowerCamelCase(name: string): string {
        // \supply\src\Supply.Scheduler.Common\Helpers\ObjectHelper.cs, ToLowerCamelCase
        let result;
        if (name.length > 0) {
            result = name[0].toLocaleLowerCase() + name.substring(1);
        } else {
            result = name;
        }

        return result;
    }

    private getIconName(customValueType: CustomValueType) {
        if (customValueType !== undefined) {
            return this.toLowerCamelCase(CustomValueType[customValueType]);
        }
    }

    private indicatorValueTitle(customValue: CustomValue): string {
        let result = customValue.value.toString();

        if (customValue.type !== undefined) {
            result = `${localize(
                `indicatorValue_${CustomValueType[customValue.type]}`
            )} ${customValue.value.toLocaleString()}`;
        }

        return result;
    }

    private shorten(value: number) {
        return shorten(value);
    }
}
